import { useEffect, useMemo, useRef, useState } from 'react';
import { useAppContext } from '../../../context/AppContext';
import { projectPickerLabel } from '../../../utils/tickets';

const MAX_RESULTS = 50;

export const ProjectPicker = ({
    projectId,
    onChange,
    required = false,
    disabled = false,
}) => {
    const { projects, isLoadingProjects } = useAppContext();
    const [query, setQuery] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    const selectedProject = useMemo(
        () => projects.find((project) => project.id === Number(projectId)) || null,
        [projects, projectId]
    );

    useEffect(() => {
        if (selectedProject) {
            setQuery(projectPickerLabel(selectedProject));
        } else if (projectId) {
            setQuery(`Project #${projectId}`);
        } else {
            setQuery('');
        }
    }, [selectedProject, projectId]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (!containerRef.current?.contains(event.target)) {
                setIsOpen(false);
                if (selectedProject) {
                    setQuery(projectPickerLabel(selectedProject));
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [selectedProject]);

    const matches = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const sorted = [...projects].sort((a, b) => {
            const nameA = (a.project_name || a.name || '').toLowerCase();
            const nameB = (b.project_name || b.name || '').toLowerCase();
            return nameA.localeCompare(nameB);
        });

        if (!needle || (selectedProject && query === projectPickerLabel(selectedProject))) {
            return sorted.slice(0, MAX_RESULTS);
        }

        return sorted
            .filter((project) => {
                const haystack = [
                    project.project_name,
                    project.name,
                    project.company_name,
                    project.id,
                ]
                    .map((value) => String(value || '').toLowerCase())
                    .join(' ');
                return haystack.includes(needle);
            })
            .slice(0, MAX_RESULTS);
    }, [projects, query, selectedProject]);

    const selectProject = (project) => {
        onChange(project.id);
        setQuery(projectPickerLabel(project));
        setIsOpen(false);
    };

    const clearProject = () => {
        onChange(null);
        setQuery('');
        setIsOpen(true);
    };

    return (
        <div className="project-picker" ref={containerRef}>
            <div className="project-picker__row">
                <input
                    id="ticket-project"
                    type="text"
                    className="form-input"
                    value={query}
                    disabled={disabled || isLoadingProjects}
                    placeholder={isLoadingProjects ? 'Loading projects...' : 'Search by name, company, or ID'}
                    autoComplete="off"
                    role="combobox"
                    aria-required={required}
                    aria-expanded={isOpen}
                    aria-controls="ticket-project-results"
                    aria-autocomplete="list"
                    onChange={(event) => {
                        const nextQuery = event.target.value;
                        setQuery(nextQuery);
                        setIsOpen(true);
                        if (selectedProject && nextQuery !== projectPickerLabel(selectedProject)) {
                            onChange(null);
                        }
                    }}
                    onFocus={() => setIsOpen(true)}
                    onKeyDown={(event) => {
                        if (event.key === 'Escape') {
                            setIsOpen(false);
                        }
                    }}
                />
                {selectedProject && !disabled && (
                    <button
                        type="button"
                        className="btn btn-secondary project-picker__clear"
                        onClick={clearProject}
                    >
                        Clear
                    </button>
                )}
            </div>
            {isOpen && !disabled && !isLoadingProjects && (
                <ul id="ticket-project-results" className="project-picker__menu" role="listbox">
                    {matches.length === 0 ? (
                        <li className="project-picker__empty">No matching projects</li>
                    ) : (
                        matches.map((project) => (
                            <li key={project.id}>
                                <button
                                    type="button"
                                    className={`project-picker__option${project.id === selectedProject?.id ? ' project-picker__option--selected' : ''}`}
                                    onClick={() => selectProject(project)}
                                    role="option"
                                    aria-selected={project.id === selectedProject?.id}
                                >
                                    {projectPickerLabel(project)}
                                </button>
                            </li>
                        ))
                    )}
                </ul>
            )}
        </div>
    );
};
